import React from "react";
import { fmtDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ClipboardCheck, Save } from "lucide-react";
import { RAG_STROKE, RAG_LABEL } from "@/lib/rag";

const RAG_BG = { green: "bg-rag-green", amber: "bg-rag-amber", red: "bg-rag-red" };
const saveLabel = (saving, dirty) => { if (saving) return "Saving…"; return dirty ? "Save progress" : "Saved"; };
const fmtDateOrNull = (iso) => (iso ? fmtDate(iso) : null);

const ChangeBadge = ({ change }) => {
  if (change == null) return "—";
  if (change > 0) return <span className="text-emerald-300">↑ {change}%</span>;
  if (change < 0) return <span className="text-red-300">↓ {Math.abs(change)}%</span>;
  return <span className="text-white/70">0%</span>;
};

const Metric = ({ testId, label, children }) => (
  <div data-testid={testId}><div className="text-white/50 text-xs">{label}</div><div className="font-semibold text-sm mt-0.5">{children}</div></div>
);

export function AssuranceHeader({ live, rag, assessed = true, complete = true, assurance, audited, sampleSize, saving, dirty, completing, onSave, onComplete, canSignOff = true }) {
  const signed = assurance?.last_score != null;
  const inProgress = assessed && !complete;
  return (
    <div data-testid="evidence-overall" className="rounded-2xl bg-brand text-white p-6 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-5">
          <div className="relative h-20 w-20 grid place-items-center">
            <svg className="h-20 w-20 -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              {assessed && <circle cx="18" cy="18" r="15.9" fill="none" stroke={inProgress ? "rgba(255,255,255,0.55)" : RAG_STROKE[rag]} strokeWidth="3" strokeDasharray={`${inProgress ? Math.round((((live.practiceAnswered ?? 0) + audited) / Math.max(1, (live.practiceTotal ?? 4) + sampleSize)) * 100) : live.overall}, 100`} strokeLinecap="round" />}
            </svg>
            <span className={`absolute font-bold font-heading text-center leading-tight ${inProgress ? "text-[11px]" : "text-xl"}`} data-testid="header-ring-label">{!assessed ? "—" : inProgress ? <>{(live.practiceAnswered ?? 0) + audited}/{(live.practiceTotal ?? 4) + sampleSize}<br /><span className="font-normal text-white/70">checks</span></> : `${live.overall}%`}</span>
          </div>
          <div>
            <div className="label-caps text-white/60">Current working assessment</div>
            <div data-testid="assurance-status" className="text-lg font-bold mt-1 flex items-center gap-2">
              {!assessed ? <><span className="h-2.5 w-2.5 rounded-full bg-white/40" />Not assessed</>
                : inProgress ? <><span className="h-2.5 w-2.5 rounded-full bg-coral" />Assessment in progress</>
                : <><span className={`h-2.5 w-2.5 rounded-full ${RAG_BG[rag]}`} />{RAG_LABEL[rag]}</>}
            </div>
            {inProgress && <div data-testid="header-progress-counts" className="text-sm font-semibold text-white/90 mt-0.5">{live.practiceAnswered ?? 0} of {live.practiceTotal ?? 4} practice areas confirmed · {audited} of {sampleSize} residents audited</div>}
            {inProgress && <div data-testid="score-for-completed" className="text-[11px] text-white/70 mt-0.5">Provisional score for completed checks — not a final assurance result: <b className="text-white/90">{live.overall}%</b></div>}
            <div className="text-xs text-white/60 mt-1" data-testid="signed-off-line">{signed ? <>Last signed-off score: <b className="text-white/90">{assurance.last_score}%</b> · {assurance.status_label}</> : "No signed-off review yet · Quarterly (risk-based)"}</div>
            {!inProgress && <div data-testid="sample-summary" className="text-xs text-white/80 mt-2 font-medium">Practice confirmed: {live.practiceAnswered ?? 0} / {live.practiceTotal ?? 4} areas · Resident sample: {audited} / {sampleSize} · Open actions: {live.actions ?? 0}</div>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="save-evidence-btn" onClick={onSave} disabled={saving || !dirty} variant="outline" className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-60">
            <Save className="h-4 w-4 mr-2" />{saveLabel(saving, dirty)}
          </Button>
          <Button data-testid="complete-assessment-btn" onClick={onComplete} disabled={completing || !canSignOff} title={canSignOff ? undefined : "Complete the outstanding assessments in Step 3 first"} className="rounded-full bg-coral hover:bg-coral-hover text-white disabled:opacity-60">
            <ClipboardCheck className="h-4 w-4 mr-2" />{completing ? "Recording…" : "Approve & sign off"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-white/10">
        <Metric testId="assurance-last" label="Last signed off">{fmtDateOrNull(assurance.last_assessment_at) || "Not yet completed"}</Metric>
        <Metric testId="assurance-next" label="Next review due">{fmtDateOrNull(assurance.next_due_at) || "—"}</Metric>
        <Metric testId="assurance-previous" label="Previous score">{assurance.previous_score != null ? `${assurance.previous_score}%` : "—"}</Metric>
        <Metric testId="assurance-change" label="Change"><ChangeBadge change={assurance.change} /></Metric>
      </div>

      {assessed && complete && live.overall < 75 && (
        <div data-testid="reaudit-banner" className="mt-4 rounded-xl bg-rag-red/25 border border-rag-red/40 px-4 py-2.5 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />Re-audit recommended within 30 days — significant gaps identified. Create an action plan below.
        </div>
      )}
    </div>
  );
}
